import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { Sparkles, ChevronRight, List, CheckCircle2 } from 'lucide-react';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PriceListSelectionPage() {
  const { t, lang, isRTL } = useLanguage();
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/price-lists?active_only=true`)
      .then(res => setPriceLists(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const getName = (item) => lang === 'ar' ? item.name_ar : item.name_en;
  const getDesc = (item) => lang === 'ar' ? item.description_ar : item.description_en;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0056A6] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="price-list-selection-page">
      {/* Hero Section */}
      <section className="relative bg-[#0056A6] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="h-4 w-4 text-white" />
              <span className="text-sm font-medium text-white/90">{t('heroSubtitle')}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-4">
              {t('heroTitle')}
            </h1>
            <p className="text-lg text-white/70 mb-4 leading-relaxed">
              {t('selectPriceListDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* Price Lists Selection */}
      <section className="container mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16" data-testid="price-lists-section">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
            {t('choosePriceList')}
          </h2>
          <p className="text-base text-slate-500 mt-2">{t('selectServiceType')}</p>
        </div>

        {priceLists.length === 0 ? (
          <p className="text-center text-slate-400 py-12">{t('noPriceLists')}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {priceLists.map((pl, idx) => (
              <Link
                key={pl.id}
                to={`/price-list/${pl.id}`}
                className={`group slide-up stagger-${idx + 1}`}
                data-testid={`price-list-card-${pl.id}`}
              >
                <div className={`rounded-2xl border-2 ${pl.is_default ? 'border-[#0056A6] bg-[#0056A6]/5' : 'border-slate-200 bg-white'} shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden hover:shadow-xl hover:border-[#0056A6] transition-all duration-300 h-full`}>
                  <div className="p-6">
                    <div className={`w-14 h-14 rounded-xl ${pl.is_default ? 'bg-[#0056A6]' : 'bg-slate-100 group-hover:bg-[#0056A6]'} flex items-center justify-center mb-4 transition-colors duration-300`}>
                      <List className={`h-7 w-7 ${pl.is_default ? 'text-white' : 'text-slate-500 group-hover:text-white'} transition-colors duration-300`} />
                    </div>
                    
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-bold text-slate-800 text-xl group-hover:text-[#0056A6] transition-colors">
                        {getName(pl)}
                      </h3>
                      {pl.is_default && (
                        <Badge className="bg-[#0056A6] text-white text-[0.65rem] shrink-0">
                          <CheckCircle2 className="h-3 w-3 me-1" />
                          {t('recommended')}
                        </Badge>
                      )}
                    </div>
                    
                    {getDesc(pl) && (
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2">{getDesc(pl)}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-slate-400 mb-4">
                      <span>{pl.category_count || 0} {t('categories')}</span>
                      <span className="text-slate-300">|</span>
                      <span>{pl.product_count || 0} {t('products')}</span>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <span className="text-sm font-semibold text-[#0056A6]">{t('viewPrices')}</span>
                      <ChevronRight className={`h-5 w-5 text-[#0056A6] group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
